import { Box } from '@mui/material'
import { C1LoadingButton } from 'c1ui/components/C1LoadingButton/C1LoadingButton'
import { RendererProps, resolveDynamicBool, resolveDynamicString } from 'components/a2ui/types'
import { evaluateButtonChecks, getByPointer, ValidationCheckType } from 'lib/a2ui/resolver'
import { ButtonComponent_ButtonVariant } from 'pbts/c1/api/a2ui/v1/components'
import { FC, useCallback, useMemo } from 'react'

type ButtonRendererProps = Pick<RendererProps, 'component' | 'dataModel' | 'onAction' | 'disabled' | 'loadingComponents'>

/**
 * Renders a button using C1LoadingButton which provides built-in loading state.
 * Handles action dispatch with context resolution from the data model.
 * Auto-disables when validation checks fail.
 */
export const ButtonRenderer: FC<ButtonRendererProps> = ({
    component,
    dataModel,
    onAction,
    disabled = false,
    loadingComponents,
}) => {
    const btn = component.component.oneofKind === 'button' ? component.component.button : undefined
    const label = useMemo(
        () => (btn ? resolveDynamicString(btn.label, dataModel, getByPointer) : ''),
        [btn, dataModel],
    )
    const isLoading = loadingComponents?.has(component.id) ?? false

    // Evaluate button checks - all must pass for button to be enabled
    const checksPass = useMemo(() => {
        if (!btn?.checks?.length) return true
        return evaluateButtonChecks(btn.checks as ValidationCheckType[], dataModel)
    }, [btn?.checks, dataModel])

    const isDisabled = disabled || isLoading || !checksPass || (btn ? resolveDynamicBool(btn.disabled, dataModel, getByPointer) : false)

    const handleClick = useCallback((): void => {
        if (!btn || btn.action?.actionType.oneofKind !== 'event') return

        const event = btn.action.actionType.event
        const resolvedContext: Record<string, string> = {}

        for (const [key, value] of Object.entries(event.context ?? {})) {
            if (value.startsWith('/')) {
                const resolved = getByPointer(dataModel, value)
                resolvedContext[key] = resolved !== undefined ? String(resolved) : ''
            } else {
                resolvedContext[key] = value
            }
        }

        onAction(event.name, resolvedContext, component.id)
    }, [btn, dataModel, onAction, component.id])

    if (!btn) return null

    const variant = btn.variant === ButtonComponent_ButtonVariant.PRIMARY ? 'contained' : 'outlined'

    return <Box>
        <C1LoadingButton
            variant={variant}
            onClick={handleClick}
            disabled={isDisabled}
            loading={isLoading}
        >
            {label}
        </C1LoadingButton>
    </Box>
}
